import type {PropertyDefinition, Repository} from '@github-ui/custom-properties-types'
import {listOrgReposPropertyValuesPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReposFilter} from '@github-ui/repos-filter'
import {type ListResults, NoReposMessage, useListResults, type UserInfo} from '@github-ui/repos-list-shared'
import {useSearchParams} from '@github-ui/use-navigate'
import {useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useSetFlash} from '../contexts/FlashContext'
import {ReposPropertiesList} from './ReposPropertiesList'

// This page can only be accessed by org admins
const adminUserInfo: UserInfo = {
  admin: true,
  // This page never allows creating repos, no matter the current user
  canCreateRepository: false,
  directOrTeamMember: true,
}

export function SetValuesTab({
  definitions,
  initialResults,
}: {
  definitions: PropertyDefinition[]
  initialResults: ListResults<Repository>
}) {
  const [initialParams] = useSearchParams()

  const [query, setQuery] = useState(initialParams.get('q') || '')
  const org = useCurrentOrg()

  const setFlash = useSetFlash()

  const {results, currentPage, isFetching, fetchResults} = useListResults<Repository>(
    initialResults,
    listOrgReposPropertyValuesPath({org: org.login}),
    Number(initialParams.get('page')),
  )

  const onEditSuccess = () => {
    fetchResults({page: currentPage, q: query})
    setFlash('repos.properties.updated')
  }

  return (
    <>
      <ReposFilter
        id="repos-list-filter"
        sx={{flex: 1, mb: 3}}
        label="Search repositories"
        placeholder="Search repositories"
        filterValue={query}
        isFetchingResults={isFetching}
        definitions={definitions}
        canUseNone={useFeatureFlag('custom_properties_es_none')}
        onChange={q => {
          setQuery(q)
          fetchResults({page: 1, q})
        }}
      />
      <div data-hpc>
        {results.repositories.length ? (
          <ReposPropertiesList
            repositoryCount={results.repositoryCount}
            pageCount={results.pageCount}
            repos={results.repositories}
            definitions={definitions}
            page={currentPage}
            query={query}
            onPageChange={page => fetchResults({page, q: query})}
            onReposSelection={() => setFlash(null)}
            onEditSuccess={onEditSuccess}
          />
        ) : (
          <NoReposMessage
            currentPage={currentPage}
            pageCount={results.pageCount}
            filtered={!!query}
            userInfo={adminUserInfo}
          />
        )}
      </div>
    </>
  )
}

try{ SetValuesTab.displayName ||= 'SetValuesTab' } catch {}